import React from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";

/**
 * EmptyState — shared empty-list placeholder used across all role dashboards.
 *
 * Purpose: turn dead-end empty lists into "here's exactly what to do next"
 * moments. Always renders the spec'd copy + a single primary CTA.
 *
 * Usage:
 *   <EmptyState
 *     title="Ready to create your first moment?"
 *     subtitle="Tell our AI Concierge what you have in mind."
 *     cta={{ to: "/ai", label: "Start Planning →", testid: "empty-state-cta" }}
 *   />
 */
export default function EmptyState({
  title,
  subtitle,
  cta,                 // { to, label, testid, onClick }
  icon: Icon = Sparkles,
  testid = "empty-state",
  tone = "muted",      // "muted" (default) or "luxury" (dark hero card)
}) {
  const isLuxury = tone === "luxury";
  return (
    <div
      data-testid={testid}
      className={`p-8 md:p-10 rounded-3xl text-center ${
        isLuxury
          ? "bg-[#071A2D] text-[#FFF8EE] border border-[#C9A45C]/30"
          : "card-luxury text-[#071A2D]"
      }`}
    >
      <div
        className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${
          isLuxury ? "bg-[#C9A45C]/20 text-[#C9A45C]" : "bg-[#064E3B]/8 text-[#064E3B]"
        }`}
      >
        <Icon size={22}/>
      </div>
      <div className={`font-serif text-2xl md:text-3xl mt-4 ${isLuxury ? "text-[#FFF8EE]" : "text-[#071A2D]"}`}>
        {title}
      </div>
      {subtitle && (
        <p className={`text-sm mt-2 max-w-md mx-auto ${isLuxury ? "text-[#FFF8EE]/75" : "text-[#071A2D]/65"}`}>
          {subtitle}
        </p>
      )}
      {cta && (
        cta.onClick ? (
          <button
            data-testid={cta.testid}
            onClick={cta.onClick}
            className={`mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition ${
              isLuxury
                ? "bg-[#C9A45C] text-[#071A2D] hover:brightness-110"
                : "bg-[#FF7A45] text-[#FFF8EE] hover:brightness-110"
            }`}
          >
            {cta.label}
          </button>
        ) : (
          <Link
            to={cta.to}
            data-testid={cta.testid}
            className={`mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition ${
              isLuxury
                ? "bg-[#C9A45C] text-[#071A2D] hover:brightness-110"
                : "bg-[#FF7A45] text-[#FFF8EE] hover:brightness-110"
            }`}
          >
            {cta.label}
          </Link>
        )
      )}
    </div>
  );
}
